import { Box, styled } from '@mui/material'
import { Typography } from '@carto/meridian-ds/components'
import { useAppSelector } from 'app/state'
import { DataExplorerTable } from 'features/data-explorer/types'
import { FormattedMessage } from 'react-intl'
import { getSubscriptionBySlug } from 'features/data-observatory/state/dataObservatorySlice'
import getValueString from 'features/common/utils/getValueString'
import DataProviderIcon from 'features/data-observatory/ui/Subscriptions/DataProviderIcon'
import EllipsisWithTooltip from 'features/common/ui/utils/EllipsisWithTooltip'

const Root = styled(Box)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  padding: theme.spacing(3),
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.background.default
}))

const Meta = styled(Typography)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  flex: '0 1 auto',
  minWidth: '1px',
  gap: theme.spacing(1),
  '&:not(:last-child)::after': {
    color: theme.palette.text.secondary,
    content: '"•"'
  }
}))

interface SubscriptionCardProps {
  table: DataExplorerTable
}

function SubscriptionCard({ table }: SubscriptionCardProps) {
  const subscription = useAppSelector((state) => getSubscriptionBySlug(state, table.slug || ''))
  const dataProvider = table.doDatasetId?.split('.')[1]

  if (!subscription) {
    return null
  }

  return (
    <Root>
      <EllipsisWithTooltip>
        <Typography variant="h6">{subscription.dataset_name}</Typography>
      </EllipsisWithTooltip>
      <Box display="flex" alignItems="center" gap={1} mt={1}>
        {dataProvider && <DataProviderIcon provider={dataProvider} />}
        <Meta variant="caption">
          <EllipsisWithTooltip>{subscription.dataset_provider}</EllipsisWithTooltip>
        </Meta>
        <Meta variant="caption">
          <EllipsisWithTooltip>{subscription.dataset_category}</EllipsisWithTooltip>
        </Meta>
        <Meta variant="caption" color="textSecondary">
          <EllipsisWithTooltip>
            <FormattedMessage
              id="data-explorer.tables.rows"
              values={{ rows: getValueString(table.subscription?.nrows, 'number-short') }}
            />
          </EllipsisWithTooltip>
        </Meta>
        <Meta variant="caption" color="textSecondary">
          <EllipsisWithTooltip>{getValueString(table.subscription?.size, 'size')}</EllipsisWithTooltip>
        </Meta>
        <Meta variant="caption" color="textSecondary">
          <EllipsisWithTooltip>{subscription.dataset_geo_type}</EllipsisWithTooltip>
        </Meta>
      </Box>
    </Root>
  )
}

export default SubscriptionCard
